import { Component, Inject, OnChanges, OnDestroy, OnInit, output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogClose,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { PbxAccountModel } from '../../../api/models/pbx-account-model';
import { NotificationService } from '../../../notification/notification.service';
import { LocalPbxService } from '../../local-pbx.service';
import { E911DetailsRequest } from '../../../api/models/e-911-details-request';
import { FlexModule } from '@angular/flex-layout';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatTextInputComponent } from '../../../editors/mat-text-input/mat-text-input.component';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { debounceTime, distinctUntilChanged, finalize, of, Subscription, switchMap, take } from 'rxjs';
import { MatAutocompleteComponent } from '../../../editors/mat-autocomplete-input/mat-autocomplete.component';
import { LookupModel } from '../../../api/models';
import { GooglePlacesService } from '../../../core/services/google-places.service';

export interface RequestE911AddressData {
  pbxAccount: PbxAccountModel;
}

@Component({
  selector: 'app-request-e911-address',
  template: `
    <h1 mat-dialog-title fxLayout="row" fxLayoutAlign="space-between center" style="padding-top: 10px;">
      <div fxFlex>Request new E-911 Address</div>
      <button mat-icon-button [mat-dialog-close]="false" class="button-40">
        <mat-icon>close</mat-icon>
      </button>
    </h1>
    <mat-dialog-content class="mat-typography">
      <form [formGroup]="form" novalidate (ngSubmit)="onSubmit()">
        <div fxLayout="column" fxLayoutAlign="start stretch" fxLayoutGap="15px">
          <app-mat-autocomplete
            label="Address 1"
            name="address1"
            placeholder="Address 1"
            [autoCompleteValues]="addressSuggestions"
            [formControl]="$any(form.get('address1'))"
            [submitted]="submitted"
            (valueChanged)="onSelectAddress($event)"
          ></app-mat-autocomplete>

          <app-mat-text-input
            fxFlex
            formControlName="address2"
            label="Address 2"
            name="address2"
            placeholder="Address 2"
            [submitted]="submitted"
          >
          </app-mat-text-input>

          <app-mat-text-input
            fxFlex
            formControlName="state"
            label="State"
            name="state"
            placeholder="State"
            [submitted]="submitted"
          >
          </app-mat-text-input>

          <app-mat-text-input
            fxFlex
            formControlName="city"
            label="City"
            name="city"
            placeholder="City"
            [submitted]="submitted"
          >
          </app-mat-text-input>

          <app-mat-text-input
            fxFlex
            formControlName="zip"
            label="Zip"
            name="zip"
            placeholder="Zip"
            [submitted]="submitted"
          >
          </app-mat-text-input>
        </div>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-stroked-button [mat-dialog-close]="false" [disabled]="submitted">Cancel</button>
      <button
        mat-raised-button
        color="primary"
        cdkFocusInitial
        (click)="onSubmit()"
        [disabled]="submitted || form.invalid"
        [promiseBtn]="submitted"
      >
        Submit
      </button>
    </mat-dialog-actions>
  `,
  styles: [],
  standalone: true,
  imports: [
    MatDialogTitle,
    FlexModule,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatTextInputComponent,
    MatDialogActions,
    MatButton,
    Angular2PromiseButtonModule,
    MatAutocompleteComponent,
  ],
})
export class RequestE911AddressComponent implements OnInit, OnDestroy, OnChanges {
  public form: UntypedFormGroup;
  public submitted: boolean;
  public buttonSubmitPromise: Promise<void>;
  public addressSuggestions: LookupModel[] = [];

  private selectedId = '';

  private readonly _subscription = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RequestE911AddressData,
    public dialogRef: MatDialogRef<RequestE911AddressComponent>,
    public dialog: MatDialog,
    public pbxService: LocalPbxService,
    private fb: UntypedFormBuilder,
    private notificationService: NotificationService,
    private googlePlacesService: GooglePlacesService,
  ) {
    this.createForm();
  }

  public ngOnInit(): void {
    this.ngOnChanges();

    this._subscription.add(this.subscribeAddressChanges());
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  public ngOnChanges(): void {
    this.submitted = false;
    this.form.reset();
  }

  public onSelectAddress(addressId: string): void {
    const selectedFromSuggestions = this.addressSuggestions.find(x => x.id === addressId);

    if (!selectedFromSuggestions) {
      return;
    }

    this.selectedId = selectedFromSuggestions?.id as string;

    this.googlePlacesService
      .getPlaceDetails(addressId)
      .pipe(take(1))
      .subscribe(place => {
        // Parse address components
        const components: Record<string, { long: string; short: string }> = {};
        place.addressComponents?.forEach(comp => {
          const type = comp.types[0];
          components[type] = {
            long: comp.longText,
            short: comp.shortText,
          };
        });

        this.form.patchValue(
          {
            address1: [components['street_number']?.long, components['route']?.long].filter(Boolean).join(' '),
            address2: components['subpremise']?.long || '',
            city:
              components['locality']?.long ||
              components['sublocality_level_1']?.long ||
              components['administrative_area_level_2']?.long ||
              '',
            state: components['administrative_area_level_1']?.short || '',
            zip: components['postal_code']?.long || '',
          },
          { emitEvent: false, onlySelf: true },
        );
      });
  }

  public async onSubmit(): Promise<void> {
    this.processSubmit();
  }

  private processSubmit(): void {
    this.submitted = true;

    const reqData = this.prepareFormModel();

    this.pbxService
      .requestE911Addresses(reqData)
      .pipe(
        take(1),
        finalize(() => {
          this.submitted = false;
        }),
      )
      .subscribe({
        next: data => {
          this.notificationService.notifySuccess('E911 agreement document created successfully');
          this.dialogRef.close(data);
        },
      });
  }

  private prepareFormModel(): E911DetailsRequest {
    const { address1, address2, city, state, zip } = (this.form.getRawValue() || {}) as E911DetailsRequest;
    return {
      address1: address1 || '',
      address2: address2 || '',
      state: state || '',
      city: city || '',
      zip: zip || '',
      extensionName: this.data?.pbxAccount?.name,
      accountId: this.data?.pbxAccount?.id,
    };
  }

  private createForm(): void {
    this.form = this.fb.group({
      address1: ['', Validators.required],
      address2: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zip: ['', Validators.required],
    });
  }

  private subscribeAddressChanges(): Subscription {
    return this.form
      .get('address1')!
      .valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => {
          if (value === this.selectedId) {
            return of([]);
          }

          return this.googlePlacesService.getSuggestions(value);
        }),
      )
      .subscribe(results => {
        this.addressSuggestions = results;
      });
  }
}
